import React, { useState } from "react";
import { FaChevronDown, FaPlus, FaTrash } from "react-icons/fa";
import { createMenuItem } from "../../../themes/headerMenu";
import CollapseSection from "./CollapseSection";

const PAGE_LINKS = [
  { value: "/", label: "Home" },
  { value: "/collection", label: "Collections" },
  { value: "/track-order", label: "Track order" },
  { value: "/about", label: "About" },
  { value: "/contact", label: "Contact" },
  { value: "/wishlist", label: "Wishlist" },
  { value: "/cart", label: "Cart" },
];

const MAX_DEPTH = 2;

const GROUPS = [
  {
    id: "layout",
    title: "Layout",
    text: "Menu position and sticky navigation.",
    keys: ["headerLayout", "enableStickyHeader"],
  },
  {
    id: "colors",
    title: "Colors",
    text: "Background, text, and icons.",
    keys: ["headerBackground", "headerTextColor"],
  },
  {
    id: "search",
    title: "Search",
    text: "The search window on every header, including phones.",
    keys: [
      "searchBackground",
      "searchTextColor",
      "searchOverlay",
      "searchInputBackground",
      "searchInputText",
      "searchCardBackground",
      "searchNameColor",
      "searchPriceColor",
      "searchPlaceholder",
      "searchRecentLabel",
      "searchProductsLabel",
      "searchClearLabel",
    ],
  },
  {
    id: "announcement",
    title: "Announcement",
    text: "The message bar above the header.",
    keys: ["showAnnouncementBar", "announcementBarText", "announcementBarBg", "announcementBarColor"],
  },
];

function moveItem(list, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row);
  return next;
}

function pageValue(link) {
  return PAGE_LINKS.some((page) => page.value === link) ? link : "__custom__";
}

function announcementOn(settings) {
  return settings.showAnnouncementBar !== false;
}

function MenuItemFields({ item, onChange }) {
  const selectedPage = pageValue(item.link);
  return (
    <div className="te-menu-fields">
      <input
        className="te-input"
        value={item.label}
        placeholder="Link name"
        aria-label="Link name"
        onChange={(event) => onChange({ ...item, label: event.target.value })}
      />
      <select
        className="te-select"
        aria-label="Link destination"
        value={selectedPage}
        onChange={(event) => {
          const next = event.target.value;
          if (next === "__custom__") {
            onChange({ ...item, link: selectedPage === "__custom__" ? item.link : "" });
            return;
          }
          onChange({ ...item, link: next });
        }}
      >
        {PAGE_LINKS.map((page) => (
          <option key={page.value} value={page.value}>
            {page.label}
          </option>
        ))}
        <option value="__custom__">Custom link</option>
      </select>
      {selectedPage === "__custom__" && (
        <input
          className="te-input"
          aria-label="Custom URL"
          value={item.link}
          placeholder="https://example.com or /collection/shirts"
          onChange={(event) => onChange({ ...item, link: event.target.value })}
        />
      )}
    </div>
  );
}

function MenuItemEditor({ item, index, count, depth, open, onToggle, onChange, onRemove, onMove }) {
  const [childOpenId, setChildOpenId] = useState(null);
  const children = item.items || [];
  const preview = children.map((child) => child.label).filter(Boolean).join(" · ");

  React.useEffect(() => {
    if (!childOpenId) return;
    if (children.some((child) => child.id === childOpenId)) return;
    setChildOpenId(null);
  }, [children, childOpenId]);

  const addChild = () => {
    const child = createMenuItem({ label: "New item", link: "/" });
    onChange({ ...item, items: [...children, child] });
    setChildOpenId(child.id);
  };

  return (
    <article className={`te-footer-column ${open ? "is-open" : ""}`}>
      <button type="button" className="te-footer-column-toggle" aria-expanded={open} onClick={onToggle}>
        <span className="te-footer-index">{index + 1}</span>
        <span className="te-footer-column-summary">
          <strong>{item.label || "Untitled"}</strong>
          <small>{preview || (depth === 0 ? "No submenu" : "Submenu item")}</small>
        </span>
        {children.length > 0 ? <span className="te-footer-link-count">{children.length}</span> : null}
        <FaChevronDown size={11} className="te-footer-chevron" />
      </button>
      {open && (
        <div className="te-footer-links">
          <MenuItemFields item={item} onChange={onChange} />
          <div className="te-footer-tool-row">
            <button type="button" className="te-footer-text-btn" disabled={index === 0} onClick={() => onMove(-1)}>
              Up
            </button>
            <button
              type="button"
              className="te-footer-text-btn"
              disabled={index === count - 1}
              onClick={() => onMove(1)}
            >
              Down
            </button>
            <button type="button" className="te-footer-text-btn is-danger" onClick={onRemove}>
              <FaTrash size={11} /> Remove
            </button>
          </div>
          {depth < MAX_DEPTH && (
            <button type="button" className="te-footer-add-link" onClick={addChild}>
              <FaPlus size={10} /> Add submenu item
            </button>
          )}
          {children.length > 0 && (
            <div className="te-menu-nested">
              {children.map((child, childIndex) => (
                <MenuItemEditor
                  key={child.id}
                  item={child}
                  index={childIndex}
                  count={children.length}
                  depth={depth + 1}
                  open={childOpenId === child.id}
                  onToggle={() => setChildOpenId(childOpenId === child.id ? null : child.id)}
                  onChange={(next) =>
                    onChange({
                      ...item,
                      items: children.map((row) => (row.id === child.id ? next : row)),
                    })
                  }
                  onRemove={() =>
                    onChange({
                      ...item,
                      items: children.filter((row) => row.id !== child.id),
                    })
                  }
                  onMove={(direction) =>
                    onChange({
                      ...item,
                      items: moveItem(children, childIndex, direction),
                    })
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function HeaderSettings({ fields, settings, renderField, menus, onMenus }) {
  const [openId, setOpenId] = useState(null);
  const byKey = Object.fromEntries((fields || []).map((field) => [field.key, field]));
  const menuPreview = (menus || []).map((item) => item.label).filter(Boolean).join(" · ");
  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  return (
    <div className="te-footer-settings">
      {GROUPS.map((group) => {
        const groupFields = group.keys
          .filter((key) => key === "showAnnouncementBar" || group.id !== "announcement" || announcementOn(settings))
          .map((key) => byKey[key])
          .filter(Boolean);
        if (groupFields.length === 0) return null;
        return (
          <CollapseSection
            key={group.id}
            title={group.title}
            text={group.text}
            open={openId === group.id}
            onToggle={() => toggle(group.id)}
          >
            {groupFields.map((field) => (
              <React.Fragment key={field.key}>{renderField(field)}</React.Fragment>
            ))}
          </CollapseSection>
        );
      })}
      <CollapseSection
        title="Menu"
        text={menuPreview || "Links across the top of the store."}
        extra={String((menus || []).length)}
        open={openId === "menu"}
        onToggle={() => toggle("menu")}
      >
        <HeaderMenuEditor menus={menus} onChange={onMenus} />
      </CollapseSection>
    </div>
  );
}

export default function HeaderMenuEditor({ menus, onChange }) {
  const [openId, setOpenId] = useState(null);

  React.useEffect(() => {
    if (!openId) return;
    if (menus.some((item) => item.id === openId)) return;
    setOpenId(null);
  }, [menus, openId]);

  const addItem = () => {
    const item = createMenuItem({ label: "New item", link: "/" });
    onChange([...menus, item]);
    setOpenId(item.id);
  };

  return (
    <div className="te-footer-columns">
      {menus.length === 0 && (
        <div className="te-footer-empty">No menu items yet. Add one for a link such as Home or Contact.</div>
      )}
      {menus.map((item, index) => (
        <MenuItemEditor
          key={item.id}
          item={item}
          index={index}
          count={menus.length}
          depth={0}
          open={openId === item.id}
          onToggle={() => setOpenId(openId === item.id ? null : item.id)}
          onChange={(next) => onChange(menus.map((row) => (row.id === item.id ? next : row)))}
          onRemove={() => onChange(menus.filter((row) => row.id !== item.id))}
          onMove={(direction) => onChange(moveItem(menus, index, direction))}
        />
      ))}
      <button type="button" className="te-add" onClick={addItem}>
        <FaPlus size={11} /> Add menu item
      </button>
    </div>
  );
}
